import { useState, useEffect, useContext } from "react";
import { FileUploader } from "react-drag-drop-files";
import { AuthContext } from "../../../Context/AuthContext";
import axios from "axios";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { Authorization } from "../../../Context/Authorization";
import Select from "react-select";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const IndividualLoan = () => {
  const [vfcNames, setVfcNames] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(false);
  const { userId, selectedJfmcType } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    member: "", loanAmount: "", loanType: "", installment: "", date: "",
    purposeOfLoan: "", cashOrBank: "", status: "AL", groupType: "Individual", uploadDocument: "",
  });
  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState("");
  const [loanTypes, setLoanTypes] = useState([]);
  const [isOtherAmount, setIsOtherAmount] = useState(false);
  const [otherAmount, setOtherAmount] = useState("");
  const Api = process.env.REACT_APP_API;
  const [selectedLoanType, setSelectedLoanType] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const fetchVfcNames = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, config);
      const data = response.data.vfcs;
      if (Array.isArray(data)) setVfcNames(data);
      else console.error("API did not return an array", data);
    } catch (error) {
      console.log("Error fetching Data", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcNames(selectedJfmcType._id);
  }, [selectedJfmcType]);

  useEffect(() => {
    setLoading(true);
    const fetchLoanTypes = async () => {
      try {
        const config = Authorization();
        const response = await fetch(`${Api}/api/loan-types`, config);
        const data = await response.json();
        if (Array.isArray(data.loanTypes)) {
          setLoanTypes(data.loanTypes);
          setFormData((prev) => ({ ...prev, loanType: selectedLoanType._id }));
        } else {
          console.error("Expected 'loanTypes' to be an array but got:", data.loanTypes);
        }
        setLoanTypes(data.loanTypes);
      } catch (error) {
        console.log("Error fetching loan types", error);
      }
      setLoading(false);
    };
    const fetchInstallments = async () => {
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/installments`, config);
        setInstallments(response.data);
      } catch (err) {
        console.error("Error fetching data", err);
      }
      setLoading(false);
    };
    fetchInstallments();
    fetchLoanTypes();
  }, []);

  const handleVfcChange = async (event) => {
    const vfcId = event.target.value;
    setSelectedVfc(vfcId);
    if (!vfcId) { setMembers([]); return; }
    setLoading(true);
    try {
      const config = Authorization();
      const response = await fetch(`${Api}/api/members/getMembersByVFC/${vfcId}`, config);
      const data = await response.json();
      setMembers(data.members);
    } catch (error) {
      console.error("Error fetching members", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, type, value, files } = event.target;
    if (type === "file") setFormData((prev) => ({ ...prev, [name]: files[0] }));
    else setFormData({ ...formData, [name]: value });
  };

  const handleLoanAmountChange = (event) => {
    const value = event.target.value;
    if (value === "Other") { setIsOtherAmount(true); }
    else { setIsOtherAmount(false); setFormData({ ...formData, loanAmount: value }); }
  };

  const handleOtherAmountChange = (event) => {
    setOtherAmount(event.target.value);
    setFormData({ ...formData, loanAmount: event.target.value });
  };

  const handleFileUpload = (file) => {
    setFormData((prev) => ({ ...prev, uploadDocument: file }));
  };

  const validateForm = () => {
    const { member, loanAmount, loanType, purposeOfLoan, cashOrBank } = formData;
    const newErrors = {};
    if (!selectedVfc) newErrors.vfc = "VFC is required";
    if (!member) newErrors.member = "Member is required";
    if (!loanAmount) newErrors.loanAmount = "Loan amount is required";
    if (!loanType) newErrors.loanType = "Loan type is required";
    if (!purposeOfLoan) newErrors.purposeOfLoan = "Purpose of loan is required";
    if (!cashOrBank) newErrors.cashOrBank = "Cash Or Bank is required";
    if (!selectedDate) newErrors.date = "Date is required";
    return newErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage("");
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }
    if (!formData.installment) { console.error("Installment is required but missing."); return; }

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      if (formData.uploadDocument) formDataToSend.append("uploadDocument", formData.uploadDocument);
      formDataToSend.append("vfcId", selectedVfc);
      formDataToSend.append("loanType", formData.loanType);
      formDataToSend.append("installment", formData.installment);
      formDataToSend.append("date", selectedDate);
      formDataToSend.append("groupType", formData.groupType || "Individual");
      formDataToSend.append("amount", isOtherAmount ? otherAmount : formData.loanAmount);
      formDataToSend.append("shgId", selectedVfc);
      formDataToSend.append("cashOrBank", formData.cashOrBank);
      formDataToSend.append("status", "AL");
      const subLoans = [{ memberId: formData.member, subLoanId: "individualSubLoan", disbursedAmount: isOtherAmount ? otherAmount : formData.loanAmount }];
      formDataToSend.append("subLoans", JSON.stringify(subLoans));

      const config = Authorization();
      const response = await fetch(`${Api}/api/loans`, {
        method: "POST",
        headers: { ...config.headers },
        body: formDataToSend,
      });
      if (!response.ok) throw new Error("Failed to submit loan application");

      setSubmitMessage("Loan application submitted successfully!");
      await logActivity(userId, "New Individual Loan Created", "Individual Loan Page", Api);

      setFormData({ member: "", loanAmount: "", loanType: loanTypes.length > 0 ? loanTypes[0]._id : "", installment: "", date: "", purposeOfLoan: "", cashOrBank: "", status: "AL", groupType: "Individual", uploadDocument: "" });
      setSubmitMessage("");
      setSelectedVfc("");
      setMembers([]);
      setIsOtherAmount(false);
      setOtherAmount("");
      setErrors({});
    } catch (error) {
      console.error("Error submitting form", error);
      setSubmitMessage("Error submitting loan application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
              <i className="mdi mdi-account-outline" />
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Individual Loan Form</span>
          </div>
          <div style={{ padding: "20px 24px" }}>
            {submitMessage && (
              <div style={{ marginBottom: 16, padding: "10px 16px", background: "#E8F5E9", border: "1px solid #C8E6C9", borderRadius: 8, fontSize: 13, color: "#1B5E20" }}>
                {submitMessage}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {/* Left column */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>JFMC Name</label>
                    <select value={selectedVfc} onChange={handleVfcChange} style={{ ...inputStyle, borderColor: errors.vfc ? "#C62828" : "#E2E8F0" }}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = errors.vfc ? "#C62828" : "#E2E8F0")}>
                      <option value="">Select JFMC</option>
                      {vfcNames.sort((a, b) => a.vfcName.localeCompare(b.vfcName)).map((vfc) => (
                        <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>
                      ))}
                    </select>
                    {errors.vfc && <div style={{ fontSize: 11, color: "#C62828", marginTop: 4 }}>{errors.vfc}</div>}
                  </div>

                  <div>
                    <label style={labelStyle}>Member</label>
                    <Select
                      placeholder="Select Member"
                      value={members.sort((a, b) => a.name.localeCompare(b.name)).map((m) => ({ value: m._id, label: `${m.name} (${m.memberCode})` })).find((opt) => opt.value === formData.member) || null}
                      onChange={(opt) => handleInputChange({ target: { name: "member", value: opt ? opt.value : "" } })}
                      options={members.map((m) => ({ value: m._id, label: `${m.name} - (${m.fatherOrHusbandName})(${m.memberCode})` }))}
                      isClearable isSearchable
                    />
                    {errors.member && <div style={{ fontSize: 11, color: "#C62828", marginTop: 4 }}>{errors.member}</div>}
                  </div>

                  <div>
                    <label style={labelStyle}>Loan Amount</label>
                    <select name="loanAmount" value={isOtherAmount ? "Other" : formData.loanAmount} onChange={handleLoanAmountChange} style={{ ...inputStyle, borderColor: errors.loanAmount ? "#C62828" : "#E2E8F0" }}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = errors.loanAmount ? "#C62828" : "#E2E8F0")}>
                      <option value="">Select Loan Amount</option>
                      <option value="5000">5,000</option>
                      <option value="10000">10,000</option>
                      <option value="15000">15,000</option>
                      <option value="Other">Other</option>
                    </select>
                    {isOtherAmount && (
                      <input type="number" placeholder="Enter Loan Amount" value={otherAmount} onChange={handleOtherAmountChange}
                        style={{ ...inputStyle, marginTop: 8 }}
                        onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                    )}
                    {errors.loanAmount && <div style={{ fontSize: 11, color: "#C62828", marginTop: 4 }}>{errors.loanAmount}</div>}
                  </div>

                  <div>
                    <label style={labelStyle}>Installment</label>
                    <select name="installment" value={formData.installment || ""} onChange={handleInputChange} style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                      <option value="">Select Installment</option>
                      {installments.sort((a, b) => a.installmentNumber - b.installmentNumber).map((installment) => (
                        <option key={installment._id} value={installment._id}>{installment.installmentNumber}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Loan Date</label>
                    <input type="date" name="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                      style={{ ...inputStyle, borderColor: errors.date ? "#C62828" : "#E2E8F0" }}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = errors.date ? "#C62828" : "#E2E8F0")} />
                    {errors.date && <div style={{ fontSize: 11, color: "#C62828", marginTop: 4 }}>{errors.date}</div>}
                  </div>

                  <div>
                    <label style={labelStyle}>Loan Type</label>
                    <select value={formData.loanType} onChange={(e) => setFormData({ ...formData, loanType: e.target.value })} style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                      <option value="">Select Loan Type</option>
                      {loanTypes.map((loan) => <option key={loan._id} value={loan._id}>{loan.loanName}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Cash Or Bank</label>
                    <select name="cashOrBank" value={formData.cashOrBank} onChange={handleInputChange} style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                      <option value="">Select</option>
                      <option value="cash">Cash</option>
                      <option value="bank">Bank</option>
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Purpose Of Loan</label>
                    <select name="purposeOfLoan"
                      value={formData.isOtherPurpose ? "Other" : formData.purposeOfLoan}
                      onChange={(e) => {
                        if (e.target.value === "Other") setFormData({ ...formData, isOtherPurpose: true, purposeOfLoan: "" });
                        else setFormData({ ...formData, isOtherPurpose: false, purposeOfLoan: e.target.value });
                      }}
                      style={{ ...inputStyle, borderColor: errors.purposeOfLoan ? "#C62828" : "#E2E8F0" }}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = errors.purposeOfLoan ? "#C62828" : "#E2E8F0")}>
                      <option value="">Select</option>
                      <option value="Agriculture">Agriculture</option>
                      <option value="Goat Farming">Goat Farming</option>
                      <option value="Poultry Farming">Poultry Farming</option>
                      <option value="Fisheries">Fisheries</option>
                      <option value="Small Business">Small Business</option>
                      <option value="Dairy Farming">Dairy Farming</option>
                      <option value="Handicrafts">Handicrafts</option>
                      <option value="Education">Education</option>
                      <option value="Medical Expenses">Medical Expenses</option>
                      <option value="House Construction/Repair">House Construction / Repair</option>
                      <option value="Marriage">Marriage</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.purposeOfLoan && <div style={{ fontSize: 11, color: "#C62828", marginTop: 4 }}>{errors.purposeOfLoan}</div>}
                    {formData.isOtherPurpose && (
                      <input type="text" placeholder="Enter Purpose" value={formData.purposeOfLoan}
                        onChange={(e) => setFormData({ ...formData, purposeOfLoan: e.target.value })}
                        style={{ ...inputStyle, marginTop: 8 }}
                        onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                    )}
                  </div>
                </div>

                {/* Right column */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Upload Documents</label>
                    <FileUploader
                      type="file"
                      multiple={false}
                      name="uploadDocument"
                      handleChange={(file) => handleFileUpload(file)}
                      types={["jpg", "png", "gif", "pdf"]}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div style={{ padding: "16px", background: "#F7FAFC", borderRadius: 8, border: "1px solid #EDF2F7" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#2D3748", marginBottom: 10 }}>Loan Issue Criteria</div>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: "#4A5568", lineHeight: 1.8 }}>
                      <li>Criteria 1</li>
                      <li>Criteria 2</li>
                      <li>Criteria 3</li>
                      <li>Criteria 4</li>
                      <li>Criteria 5</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 20 }}>
                <button type="submit"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
                  <i className="mdi mdi-file-check" /> Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualLoan;
